import {memo, useMemo, useCallback} from 'react';
import {Analytics, Palette} from 'hubbl-shared';
import {WhiteContainer, TextHeading3} from '../index';
import {useNavigate} from 'react-router-dom';
import {useAppSelector, useI18n} from '../../hooks';
import {AdminWebRoute} from '../../routes/AdminWebRoute';
import styled from 'styled-components';
import {
  HorizontalProgressBars,
  ProgressBarData,
} from '../../scenes/auth/admin/interest-analytics/components/HorizontalProgressBars';
import {GlobalSelector} from '../../store/global/GlobalSelector';

interface DashboardTopInterestsProps {
  analytics: Analytics.InterestAnalytics;
  showHeader?: boolean;
}

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const COLORS = [
  Palette.primaryTerraCotta,
  Palette.primaryCherokee,
  Palette.primaryBianca,
  Palette.primaryDustySky,
  Palette.primaryPadua,
];

export const DashboardTopInterests = memo(
  ({analytics, showHeader = true}: DashboardTopInterestsProps) => {
    const navigate = useNavigate();
    const t = useI18n();
    const prefLanguage = useAppSelector(GlobalSelector.getPreferredLanguage);
    const handleInterestClick = useCallback(() => {
      navigate(`/admin/${AdminWebRoute.InterestAnalytics}`);
    }, [navigate]);
    const chartData = useMemo(() => {
      if (!analytics.interestCounts) return [];

      // Convert interest counts to chart data and sort by count
      const data = Object.entries(analytics.interestCounts)
        .map(([interestId, interestData]) => ({
          interest: interestData.interest.label[prefLanguage] || interestId,
          emoji: interestData.interest.emoji || '💡',
          count: interestData.count,
          percentage: interestData.percentage,
          userIds: interestData.userIds,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10); // Top 10

      return data;
    }, [analytics.interestCounts]);

    const progressBarData: ProgressBarData[] = useMemo(() => {
      return chartData.map((item, index) => ({
        id: item.interest,
        label: `${item.emoji} ${item.interest}`,
        count: item.count,
        color: COLORS[index % COLORS.length],
        onClick: handleInterestClick,
      }));
    }, [chartData, handleInterestClick]);

    if (chartData.length === 0) {
      return (
        <>
          {showHeader && (
            <SectionHeader>
              <TextHeading3 text={t.common.topInterests} />
            </SectionHeader>
          )}
          <WhiteContainer>
            <div style={{padding: '40px', textAlign: 'center', color: '#999'}}>
              {t.common.noInterestDataAvailable}
            </div>
          </WhiteContainer>
        </>
      );
    }

    return (
      <>
        {showHeader && (
          <SectionHeader>
            <TextHeading3 text={t.common.topInterests} />
          </SectionHeader>
        )}
        <WhiteContainer>
          <HorizontalProgressBars data={progressBarData} />
        </WhiteContainer>
      </>
    );
  },
);
